import { API_ROUTES } from "@/constants/api";
import {
  DEFAULT_INVOICE_ASSET_FORMAT,
  DEFAULT_INVOICE_PAGE_SIZE,
  INVOICE_ASSET_FORMATS,
  INVOICE_IMAGE_SCALE,
  buildInvoiceAssetKey,
  isWorkspaceScopedKey,
  type InvoiceAssetFormat,
  type InvoicePageSizeId,
} from "@bizy/core";
import { apiUpload } from "@/lib/api-client";
import { toBlob } from "html-to-image";

/**
 * Invoice image pipeline (PRD §3.2.4): render the sheet in the DOM → rasterise →
 * upload through the Worker storage gateway → bind the asset to the invoice.
 * The bucket credential never reaches the browser and no signed URL is involved
 * (docs/adr/0001-worker-mediated-storage.md).
 */

export interface RenderInvoiceImageInput {
  element: HTMLElement;
  format?: InvoiceAssetFormat;
  scale?: number;
}

export const renderInvoiceImage = async ({
  element,
  format = DEFAULT_INVOICE_ASSET_FORMAT,
  scale = INVOICE_IMAGE_SCALE,
}: RenderInvoiceImageInput): Promise<Blob> => {
  const options = {
    pixelRatio: scale,
    cacheBust: true,
    backgroundColor: "#ffffff",
  };

  const blob = await toBlob(element, options);
  if (!blob) throw new Error("Could not rasterise the invoice.");

  const mime = INVOICE_ASSET_FORMATS[format].mime;
  if (blob.type === mime) return blob;
  return new Blob([await blob.arrayBuffer()], { type: mime });
};

export interface UploadInvoiceAssetInput {
  workspaceId: string;
  invoiceId: string;
  pageSize: InvoicePageSizeId | string;
  format: InvoiceAssetFormat;
  blob: Blob;
}

export interface UploadedAsset {
  key: string;
  bytes: number;
  mime: string;
}

export const uploadInvoiceAsset = async ({
  workspaceId,
  invoiceId,
  pageSize = DEFAULT_INVOICE_PAGE_SIZE,
  format,
  blob,
}: UploadInvoiceAssetInput): Promise<UploadedAsset> => {
  const key = buildInvoiceAssetKey({ workspaceId, invoiceId, pageSize, format: INVOICE_ASSET_FORMATS[format].extension });
  if (!isWorkspaceScopedKey(key, workspaceId)) {
    throw new Error("Refusing to upload outside the workspace prefix.");
  }

  const formData = new FormData();
  formData.set("workspace_id", workspaceId);
  formData.set("key", key);
  formData.set("file", blob, key.split("/").pop() ?? "invoice.png");

  return apiUpload<UploadedAsset>(API_ROUTES.storageObjects, formData);
};

export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

export const invoiceAssetFilename = (
  invoiceNumber: string,
  format: InvoiceAssetFormat = DEFAULT_INVOICE_ASSET_FORMAT,
): string => `${invoiceNumber}.${INVOICE_ASSET_FORMATS[format].extension}`;
